import * as React from 'react';

import moment from 'moment';

import TailwindBaseModal from '~/UI/Tailwind/TailwindBaseModal';

import { ApiResult } from '@mam/main/ApiHooks';
import type { ErpCountriesWithPriceTypeResponse } from 'com.cogvio.pricemonitor.api';
import { ErpCountriesWithPriceTypeError } from '../api/ErpCountriesWithPriceTypeRequest';
import ERPInterventionModalRow, { InterventionState } from './ERPInterventionModalRow';
import {
	ERPScenarioCreateError,
	ERPScenarioCreateRequest,
	getHumanReadableErrorCode,
} from '../api/ERPScenarioCreateRequest';
import { ERPInterventionCreateRequest, ERPInterventionEditData } from '../api/ERPInterventionCreateRequest';
import { useApi } from '@mam/main/ApiHooks';
import { Failure } from '@mam/main/Result';

import classNames from 'classnames';
import { sortCountries } from '~/MedicinalBrandsAnalytics/Region/reducer';

import type { ERPRequestApiResult } from '../api/ERPSessionRequest';
import type { ErpScenarioResponse, ErpScenarioInterventionResponse } from 'com.cogvio.pricemonitor.api';
import ERPCancelButton from '../ERPCancelButton';
import ERPSaveButton from '../ERPSaveButton';
import { selectInputOnFocus } from '../utils/input';
import { Country } from '~/Api/Country';
import ERPModalPriceDotLegend from '~/ERP/ERPModalPriceDotLegend';

const NO_MONTHS = 4 * 12;

export type Props = {
	currency: string;
	erpDate: Date;
	erpCountriesResult: ApiResult<ErpCountriesWithPriceTypeResponse, ErpCountriesWithPriceTypeError>;
	erpSessionResult: ERPRequestApiResult;
	erpPackId: string;
	erpSessionId: string;
	scenario?: ErpScenarioResponse | null;
	userId: string;
	onClose: () => void;
	onSubmit?: () => void | Promise<void>;
};

const ERPInterventionScenarioModal = ({
	currency,
	erpCountriesResult,
	erpDate,
	erpPackId,
	erpSessionId,
	erpSessionResult,
	scenario,
	userId,
	onClose,
	onSubmit,
}: Props) => {
	const [loading, setLoading] = React.useState<boolean>(false);
	const [nameErrors, setNameErrors] = React.useState<Set<string>>(new Set());
	const [scenarioName, setScenarioName] = React.useState<string>(scenario?.name || '');
	const nameInputRef = React.useRef<HTMLInputElement | null>(null);
	const [interventionState, setInterventionState] = React.useState<InterventionState>(
		(scenario?.interventions || []).reduce(
			(all: InterventionState, data) => ({
				...all,
				[data.country.alpha2]: (all[data.country.alpha2] || []).concat([
					{
						type: data.type,
						month: data.month as unknown as string,
						price: data.price
							? {
									...data.price,
									value: typeof data.price.value === 'number' ? data.price.value.toFixed(2) : data.price.value,
							  }
							: null,
					},
				]),
			}),
			{}
		)
	);
	const months = React.useMemo(
		() =>
			Array.from(Array(NO_MONTHS)).map((_, index) => {
				const date = moment().startOf('month');

				return date.add(index + 1, 'months');
			}),
		[]
	);

	const hasNameError = nameErrors.size > 0 || scenarioName.length === 0;
	const addingScenario = !scenario;

	const [scenarioEditFetch] = useApi<
		ERPInterventionCreateRequest,
		ErpScenarioInterventionResponse,
		ERPScenarioCreateError
	>();
	const [scenarioCreateFetch] = useApi<ERPScenarioCreateRequest, ErpScenarioResponse, ERPScenarioCreateError>();

	const onModalSubmit = (nextScenarioName: string) => {
		const interventionStateEntries = Object.entries(interventionState);
		const hasInputErrors = interventionStateEntries.some(([_, data]) => data.find(({ error }) => Boolean(error)));

		if (hasNameError || hasInputErrors || !userId) {
			if (hasNameError) {
				nameInputRef.current?.focus();
			}

			return;
		}

		setLoading(true);

		const scenarioEditData = interventionStateEntries.reduce(
			(aggreagator: ERPInterventionEditData[], [countryCode, data]) => [
				...aggreagator,
				...data.map((countryData) => {
					const { error, ...apiData } = countryData;

					return { countryCode, ...apiData };
				}),
			],
			[]
		);

		if (scenario) {
			scenarioEditFetch(
				new ERPInterventionCreateRequest(userId, erpPackId, erpSessionId, scenario?.id, scenarioEditData)
			)
				.then(() => onSubmit?.())
				.then(onClose);

			return;
		}

		scenarioCreateFetch(new ERPScenarioCreateRequest(userId, erpPackId, erpSessionId, nextScenarioName)).then(
			(result) => {
				if (result instanceof Failure) {
					setNameErrors(result.error?.codes || new Set());
					setLoading(false);
					return;
				}

				const scenarioId = result.value?.id;

				if (Object.keys(interventionState).length === 0 || !scenarioId) {
					return Promise.resolve(onSubmit?.()).then(() => {
						setLoading(false);
						onClose();
					});
				}

				scenarioEditFetch(
					new ERPInterventionCreateRequest(userId, erpPackId, erpSessionId, scenarioId, scenarioEditData)
				)
					.then(() => onSubmit?.())
					.then(() => {
						setLoading(false);
						onClose();
					});
			}
		);
	};

	return (
		<TailwindBaseModal
			disabled={loading}
			footer={
				<>
					<ERPCancelButton onClick={onClose}>Close</ERPCancelButton>
					<ERPSaveButton loading={loading} type='submit'>
						Save &amp; execute model
					</ERPSaveButton>
				</>
			}
			title={addingScenario ? 'Add scenario' : 'Edit scenario'}
			onClose={onClose}
			onSubmit={() => {
				onModalSubmit(scenarioName);
			}}
		>
			<div className='px-3 pb-4 bg-white overflow-auto'>
				<div className='pb-4 flex items-center'>
					{addingScenario ? (
						<div className='flex flex-col'>
							<div className='flex flex-row items-center'>
								<h2 className='mr-2 font-display text-label font-semibold text-cPurple-600 tracking-wider'>Name:</h2>
								<input
									autoFocus
									className={classNames(
										'w-72 px-2 py-1.5 font-display text-label text-black font-semibold leading-4 border  rounded-sm tabular-nums bg-white',
										{ 'border-gray-500': !hasNameError, 'border-red-600 text-red-600': hasNameError }
									)}
									defaultValue={scenarioName}
									disabled={loading}
									placeholder='A new scenario name'
									ref={nameInputRef}
									style={hasNameError ? { outlineColor: 'red' } : {}}
									type='text'
									onFocus={selectInputOnFocus}
									onChange={(event) => {
										setScenarioName(event.currentTarget.value);
										setNameErrors(new Set());
									}}
								/>
							</div>
							{hasNameError && (
								<div className='text-red-600'>
									{getHumanReadableErrorCode(nameErrors).map((readableError) => (
										<p className='py-1 text-labelsmall'>{readableError}</p>
									))}
								</div>
							)}
						</div>
					) : (
						<span className='w-72 -mx-3 py-1.5 font-display text-black font-semibold leading-4'>{scenario?.name}</span>
					)}
				</div>
				<div className='-mx-3'>
					<table cellPadding='0' cellSpacing='0' width='100%'>
						<thead>
							<tr>
								<th className='pl-3 pr-2 py-1.5 text-left font-display text-label font-semibold text-gray-800 leading-none border-b-2 border-gray-300'>
									Country
								</th>
								<th className='pl-2 py-1.5 text-left font-display text-label font-semibold text-gray-800 leading-none border-b-2 border-gray-300'>
									Intervention
								</th>
								<th className='px-2 py-1.5 text-left font-display text-label font-semibold text-gray-800 leading-none border-b-2 border-gray-300'>
									Price [{currency}]
								</th>
								<th className='pl-2 pr-3 py-1.5 text-left font-display text-label font-semibold text-gray-800 leading-none border-b-2 border-gray-300 whitespace-nowrap text-right'>
									Date
								</th>
							</tr>
						</thead>
						<tbody>
							{sortCountries(erpCountriesResult.value?.items || []).map((country) => (
								<ERPInterventionModalRow
									country={new Country(country.alpha2, country.name)}
									currency={currency}
									erpDate={erpDate}
									erpSessionResult={erpSessionResult}
									interventionState={interventionState}
									key={country.alpha2}
									months={months}
									onChange={setInterventionState}
								/>
							))}
						</tbody>
					</table>
				</div>
				<ERPModalPriceDotLegend />
			</div>
		</TailwindBaseModal>
	);
};

export default React.memo(ERPInterventionScenarioModal);
